import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { usePayoutsList } from '../../../hooks/usePayoutsList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import PaymentRowCard from '../../../components/payments-v2/PaymentRowCard';
import CommissionBreakdownPanel from '../../../components/payments-v2/CommissionBreakdownPanel';

/**
 * Manager V2 Payments — Vue paiements/commissions du manager connecté.
 * Route : /manager/payments + /manager/v2/payments.
 * Feature : pages.managers.payments_view.
 *
 * Scope: payouts filtered by agent_id = userProfile.id.
 */
const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

const ManagerV2Payments = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  const { rows, decryptionAvailable, loading, error, reload } = usePayoutsList({ agentId: userProfile?.id || null });

  const byYear = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const list = map.get(r.period_year) || [];
      list.push(r);
      map.set(r.period_year, list);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [rows]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('paymentsV2.managerTitle', 'My payments')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {tenantName} · {userProfile?.full_name || userProfile?.email}
            </p>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {!decryptionAvailable && (
            <div className="rounded-2xl bg-accent/5 border border-accent/20 p-4 text-xs text-muted-foreground">
              <span className="font-semibold text-accent mr-1">{t('paymentsV2.aesNoteLabel', 'AES heads-up:')}</span>
              {t(
                'paymentsV2.aesNoteEdgeMissing',
                'Decryption Edge Function is not reachable. Amounts stay hidden until the function is deployed and ENCRYPTION_KEY_GENERIC is set.'
              )}
            </div>
          )}

          {loading && rows.length === 0 ? (
            <div className="space-y-3">
              <div className="h-20 skeleton rounded-2xl" />
              <div className="h-20 skeleton rounded-2xl" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('paymentsV2.emptyManager', 'No payouts recorded for your scope yet.')}
            </div>
          ) : (
            byYear.map(([year, list]) => (
              <section key={year} className="space-y-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground/70 font-semibold px-1">
                  {year}
                </div>
                <div className="space-y-2">
                  {list.map((g) => {
                    const monthLabel = t(`months.${g.period_month - 1}`, MONTHS[g.period_month - 1]);
                    const title = `${monthLabel} ${g.period_year}`;
                    const subtitle = `${g.creator_count} ${t('paymentsV2.creators', 'creators')}`;
                    const isExpanded = expandedKey === g.key;
                    return (
                      <PaymentRowCard
                        key={g.key}
                        title={title}
                        subtitle={subtitle}
                        amount={g.totalAmount}
                        status={g.status}
                        expanded={isExpanded}
                        onClick={() => setExpandedKey(isExpanded ? null : g.key)}
                      >
                        <CommissionBreakdownPanel
                          breakdown={g.creators.map((c) => ({
                            creator_id: c.creator_id,
                            creator_name: c.creator_name || (c.creator_id ? `${t('paymentsV2.creator', 'Creator')} ${c.creator_id.slice(0, 8)}` : '—'),
                            diamonds: c.diamonds,
                            amount: c.amount,
                            commission: c.commission,
                            status: c.status,
                          }))}
                          creatorBasePath="/manager/team"
                          summary={{ totalAmount: g.totalAmount, totalCommission: g.totalCommission, count: g.creator_count }}
                          loading={false}
                        />
                      </PaymentRowCard>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default ManagerV2Payments;
